import { fetchAuthSession } from '@aws-amplify/auth';
import * as Sentry from '@sentry/react';
import React, { use, useCallback, useEffect, useState } from 'react';
import { ReadyState } from 'react-use-websocket/dist/lib/constants';
import { WebSocketMessage } from 'react-use-websocket/dist/lib/types';
import { useWebSocket } from 'react-use-websocket/dist/lib/use-websocket';
import { MeContext } from './me.context';
import { WSContext } from './ws.context';

type Props = {
  children: React.ReactNode;
};

const WebsocketContext = ({ children }: Props) => {
  const { sessionId, me, selectedLocationFull } = use(MeContext);
  const [subscriptions, setSubscriptions] = useState<{
    [key: string]: (message: WebSocketMessage) => void;
  }>({});
  const getSocketUrl = useCallback(async () => {
    const { accessToken } = (await fetchAuthSession()).tokens ?? {};

    return `${import.meta.env.VITE_WEBSOCKET_URL}?token=${accessToken}&sessionId=${sessionId}`;
  }, [sessionId]);

  const { readyState, sendJsonMessage } = useWebSocket<any | null>(getSocketUrl, {
    share: true,
    shouldReconnect: () => true,
    retryOnError: true,
    reconnectAttempts: 100,
    reconnectInterval: atmpt => Math.min(Math.pow(2, atmpt) * 1000, 60000),
    onMessage: message => {
      const messageData = JSON.parse(message.data || '{}');
      const { subscription, ...data } = messageData;

      const callback = subscriptions[subscription];

      if (callback) {
        callback(data);
      } else {
        console.warn('[WS] No callback found for subscription:', subscription);
      }
    },
    onError: event => {
      console.error('WebSocket error:', event);
    },
    onClose: event => {
      console.error('WebSocket closed:', event);
    },
  });

  const connectionStatus = {
    [ReadyState.CONNECTING]: 'Connecting',
    [ReadyState.OPEN]: 'Open',
    [ReadyState.CLOSING]: 'Closing',
    [ReadyState.CLOSED]: 'Closed',
    [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
  }[readyState];

  const addSubscription = useCallback(
    (subscription: string, callback: (message: WebSocketMessage) => void) => {
      setSubscriptions(prev => {
        const newSubs = {
          ...prev,
          [subscription]: callback,
        };

        return newSubs;
      });
    },
    [readyState, connectionStatus]
  );

  const removeSubscription = useCallback((subscription: string) => {
    setSubscriptions(prev => {
      const { [subscription]: _, ...rest } = prev;

      return rest;
    });
  }, []);

  useEffect(() => {
    if (readyState === 1) {
      const subscriptionKeys = Object.keys(subscriptions);

      try {
        sendJsonMessage({
          action: 'subscriptions',
          sessionId,
          userId: me?.ID,
          clinicId: me?.selectedClinic?.ID,
          locationId: selectedLocationFull?.ID,
          subscriptions: subscriptionKeys,
        });
      } catch (error) {
        console.error('[WS] Error sending subscriptions:', error);
        Sentry.captureException(error);
      }
    }
  }, [
    readyState,
    sessionId,
    subscriptions,
    me?.ID,
    me?.selectedClinic?.ID,
    selectedLocationFull?.ID,
    sendJsonMessage,
  ]);

  return (
    <WSContext.Provider value={{ addSubscription, removeSubscription, connectionStatus }}>
      {children}
    </WSContext.Provider>
  );
};

export default WebsocketContext;
